import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["messages"];
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { formatHubGptThreadsToCopilotThreads } from 'chatspot-core/messages/api/formatHubGptThreadsApi';
import { initializeAblyChannel } from '../../chat/actions/initializeAblyChannel';
import { sortThreadsByNewest } from '../../utils/sortThreadsByNewest';
import { copilotV2DeleteThread } from '../actions/copilotV2DeleteThread';
import { copilotV2GetLatestOrCreateThread } from '../actions/copilotV2GetLatestOrCreateThread';
import { copilotV2SoftDeleteAndGetLatestOrCreateNew } from '../actions/copilotV2SoftDeleteAndGetLatestOrCreateNew';
import { createThread } from '../actions/createThread';
import { deleteSalesCopilotThread } from '../actions/deleteSalesCopilotThread';
import { getBoxId } from '../actions/getBoxId';
import { getStartupThread } from '../actions/getStartupThread';
import { mergeThreadWithPrevious } from '../actions/mergeThreadWithPrevious';
import { getSalesCopilotThreads } from '../api/hubGptApi';
import { deleteThreadById, fetchAllThreads, getListForPortalUser, getThreadMetadata } from '../api/threadsApi';
import { convertCopilotV2ThreadsToCopilotV1ThreadsForRedux } from './copilotV2ResponseConversions';
import { copilotV1DeleteFromV2Thread } from '../actions/copilotV1DeleteFromV2Thread';
export const fetchThreads = createAsyncThunk('threads/fetchThreads', async ({
  hasSalesCopilotAccess,
  isUngatedForCopilotV2 = false
}, {
  rejectWithValue
}) => {
  try {
    if (isUngatedForCopilotV2) {
      if (hasSalesCopilotAccess) {
        const [salesCopilotThreadsResponse, copilotThreadsResponse] = await Promise.all([getSalesCopilotThreads(), getListForPortalUser({})]);
        const response = [...formatHubGptThreadsToCopilotThreads(salesCopilotThreadsResponse.threads || []), ...convertCopilotV2ThreadsToCopilotV1ThreadsForRedux(copilotThreadsResponse.threads)];
        const sortedResponse = sortThreadsByNewest(response);
        return sortedResponse;
      } else {
        const response = await getListForPortalUser({});
        return convertCopilotV2ThreadsToCopilotV1ThreadsForRedux(response.threads);
      }
    } else if (hasSalesCopilotAccess) {
      const [salesCopilotThreadsResponse, copilotThreadsResponse] = await Promise.all([getSalesCopilotThreads(), fetchAllThreads()]);
      const response = [...formatHubGptThreadsToCopilotThreads(salesCopilotThreadsResponse.threads || []), ...copilotThreadsResponse.results];
      const sortedResponse = sortThreadsByNewest(response);
      return sortedResponse;
    } else {
      const response = await fetchAllThreads();
      return response.results;
    }
  } catch (error) {
    return rejectWithValue(error);
  }
});
export const deleteThread = createAsyncThunk('threads/deleteThread', id => {
  return deleteThreadById(id).then(() => {
    return id;
  });
});
export const fetchThreadMetadata = createAsyncThunk('threads/fetchThreadMetadata', ({
  id
}) => getThreadMetadata(id));
const initialState = {
  createThreadStatus: 'idle',
  fetchThreadsStatus: 'idle',
  searchQuery: '',
  threads: []
};
const threadsSlice = createSlice({
  name: 'threads',
  initialState,
  reducers: {
    searchQueryChanged: (state, action) => {
      state.searchQuery = action.payload;
    },
    // TODO - @mlegocki: remove this in follow-up PR as part of Git: https://git.hubteam.com/HubSpot/copilot-issues/issues/126
    threadTitleUpdate: (state, action) => {
      const threadToUpdate = state.threads.find(thread => action.payload.id === thread.id);
      if (threadToUpdate) {
        threadToUpdate.label = action.payload.title;
      }
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchThreads.pending, state => {
      state.fetchThreadsStatus = 'loading';
    });
    builder.addCase(fetchThreads.rejected, state => {
      state.fetchThreadsStatus = 'failed';
      state.threads = [];
    });
    builder.addCase(fetchThreads.fulfilled, (state, action) => {
      state.fetchThreadsStatus = 'succeeded';
      state.threads = action.payload;
    });
    builder.addCase(createThread.pending, state => {
      state.createThreadStatus = 'loading';
    });
    builder.addCase(createThread.rejected, state => {
      state.createThreadStatus = 'failed';
    });
    builder.addCase(createThread.fulfilled, (state, action) => {
      state.threads.unshift(action.payload);
      state.createThreadStatus = 'succeeded';
    });

    // TODO - @mlegocki: remove this in follow-up PR as part of Git: https://git.hubteam.com/HubSpot/copilot-issues/issues/126
    builder.addCase(initializeAblyChannel.pending, state => {
      state.createThreadStatus = 'loading';
    });
    builder.addCase(initializeAblyChannel.rejected, state => {
      state.createThreadStatus = 'failed';
    });
    builder.addCase(initializeAblyChannel.fulfilled, (state, action) => {
      state.threads.unshift({
        assistantType: action.payload.thread.assistantType,
        boxId: undefined,
        createdAt: action.payload.thread.createdAt,
        customCopilotId: action.payload.thread.customCopilotId,
        id: action.payload.thread.id,
        label: action.payload.thread.title,
        labelStatus: 'INITIALIZED',
        lastMessageAt: 0,
        // TODO - is this needed for CopilotV2?
        lastMessageId: null,
        // TODO - @mlegocki: remove this if this is deprecated and not needed for CopilotV2
        userId: action.payload.thread.userId,
        portalId: action.payload.thread.portalId
      });
      state.createThreadStatus = 'succeeded';
    });
    builder.addCase(getStartupThread.fulfilled, (state, action) => {
      const _action$payload = action.payload,
        metadata = _objectWithoutPropertiesLoose(_action$payload, _excluded);
      state.threads = state.threads.filter(thread => thread.id !== metadata.id);
      state.threads.unshift(metadata);
    });

    // TODO - @mlegocki: remove this in follow-up PR as part of Git: https://git.hubteam.com/HubSpot/copilot-issues/issues/126
    builder.addCase(copilotV2GetLatestOrCreateThread.fulfilled, (state, action) => {
      const {
        thread
      } = action.payload;
      state.threads = state.threads.filter(threadFromState => threadFromState.id !== thread.id);
      state.threads.unshift(thread);
    });
    builder.addCase(deleteThread.fulfilled, (state, action) => {
      state.threads = state.threads.filter(thread => thread.id !== action.payload);
    });
    builder.addCase(deleteSalesCopilotThread.fulfilled, (state, action) => {
      state.threads = state.threads.filter(thread => thread.id !== Number(action.meta.arg));
    });

    // copilotV2DeleteThread
    builder.addCase(copilotV2DeleteThread.fulfilled, (state, action) => {
      state.threads = state.threads.filter(thread => thread.id !== Number(action.meta.arg.threadId));
    });
    builder.addCase(copilotV1DeleteFromV2Thread.fulfilled, (state, action) => {
      state.threads = state.threads.filter(thread => thread.id !== Number(action.meta.arg));
    });
    builder.addCase(getBoxId.fulfilled, (state, action) => {
      state.threads = state.threads.map(thread => thread.id === action.meta.arg ? Object.assign({}, thread, {
        boxId: action.payload.maybeBoxId
      }) : thread);
    });
    builder.addCase(fetchThreadMetadata.fulfilled, (state, action) => {
      const threadToUpdate = state.threads.find(thread => action.payload.id === thread.id);
      if (threadToUpdate) {
        // If this is used on nested objects, it will cause unexpected re-renders, even if the data is the same
        Object.assign(threadToUpdate, action.payload);
      }
    });
    builder.addCase(mergeThreadWithPrevious.fulfilled, (state, action) => {
      state.threads = state.threads.filter(thread => thread.id !== action.meta.arg.threadId);
      state.threads.push(action.payload);
    });
    builder.addCase(copilotV2SoftDeleteAndGetLatestOrCreateNew.fulfilled, (state, action) => {
      state.threads = state.threads.filter(thread => thread.id !== action.meta.arg.threadIdToDelete);
      if (action.payload.thread.isAutoCreated) {
        state.threads.push(action.payload.thread);
      } else {
        state.threads.map(thread => {
          if (thread.id === action.payload.thread.id) {
            return action.payload.thread;
          }
          return thread;
        });
      }
    });
  }
});
export const {
  searchQueryChanged,
  threadTitleUpdate
} = threadsSlice.actions;
export default threadsSlice.reducer;